import React, { useState } from 'react';
import { CheckCircle2Icon, ClockIcon, Loader2Icon, LockIcon, MailIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { services } from '../data/services';
import { company } from '../data/company';
const states = ['Arizona', 'California', 'Colorado', 'Nevada', 'Oregon', 'Texas', 'Washington', 'Other'];
const nextSteps = [{
  icon: MessageSquareIcon,
  title: 'A straightforward conversation',
  body: 'We start by understanding the situation and the responsibilities involved — no obligation.'
}, {
  icon: LockIcon,
  title: 'Handled privately',
  body: 'Inquiries are treated discreetly and shared only with the people who need to be involved.'
}, {
  icon: ClockIcon,
  title: 'A clear next step',
  body: 'We will explain which services apply and what working together would look like.'
}];
const WEB3FORMS_ACCESS_KEY = '13451597-a00d-4422-8b5b-f5314d13c39b';
const inputClass = 'mt-2 w-full border border-slate-300 bg-white px-4 py-3 text-[15px] text-ink outline-none transition-colors duration-150 ease-out focus:border-navy-900';
const labelClass = 'block text-[14px] font-medium text-navy-900';
const emptyForm = {
  name: '',
  email: '',
  phone: '',
  state: '',
  service: '',
  method: 'email',
  message: '',
  botcheck: ''
};
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const update = (field: keyof typeof form, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please provide your name, email address and a short message.');
      return;
    }
    if (form.botcheck) {
      return;
    }
    setError('');
    setSubmitting(true);
    const selectedService = services.find(service => service.slug === form.service);
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `Website inquiry from ${form.name.trim()}`,
          from_name: company.name,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          state: form.state,
          service_of_interest: selectedService?.title ?? (form.service === 'not-sure' ? 'Not sure yet' : ''),
          preferred_contact_method: form.method === 'phone' ? 'Phone' : 'Email',
          message: form.message.trim(),
          botcheck: form.botcheck
        })
      });
      const result = (await response.json()) as {
        success?: boolean;
        message?: string;
      };
      if (response.ok && result.success) {
        setForm(emptyForm);
        setSubmitted(true);
      } else {
        setError(result.message ?? `Your message could not be sent. Please try again or email us at ${company.email}.`);
      }
    } catch {
      setError(`Your message could not be sent. Please check your connection, or email us directly at ${company.email}.`);
    } finally {
      setSubmitting(false);
    }
  };
  return <main data-id="element-183" data-mp="314zi1rtn5px1">
      <PageHero breadcrumb="Contact Us" eyebrow="CONTACT US" title="Let's Discuss How We Can Help" description="Every individual and family has different needs. Contact us to discuss the type of fiduciary or administrative support you require." data-id="element-184" data-mp="3157wvp9br5av" />

      <section className="bg-mist" data-id="element-185" data-mp="1tre7bravrzik">
        <div className="mx-auto grid max-w-shell gap-12 px-6 py-20 lg:grid-cols-[1.25fr_0.75fr] lg:gap-16" data-id="element-186" data-mp="1tsfuuv932eh6">
          <div className="border border-slate-200 bg-white p-8 sm:p-10" data-id="element-187" data-mp="29zbym3070ff7">
            {submitted ? <div className="flex flex-col items-start" data-id="element-188" data-mp="255zo0wscm95g">
                <CheckCircle2Icon className="h-8 w-8 text-gold" aria-hidden="true" data-id="element-189" data-mp="207g2uylkx1e5" />
                <h2 className="mt-5 font-serif text-[28px] text-navy-900" data-id="element-190" data-mp="1v9mv414mnzbr">
                  Thank you — your message has been received
                </h2>
                <p className="mt-4 max-w-lg text-[16px] leading-relaxed text-ink/75" data-id="element-191" data-mp="2susn3249ox4a">
                  A member of our team will respond within one business day using your preferred
                  contact method. If the matter is time-sensitive, please call{' '}
                  {company.phoneDisplay}.
                </p>
                <button type="button" onClick={() => setSubmitted(false)} className="mt-8 border border-navy-900 px-6 py-3 text-[15px] font-medium text-navy-900 transition-colors duration-150 ease-out hover:bg-navy-900 hover:text-white" data-id="element-192" data-mp="17jaf02z8awa3">
                  Send another message
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate data-id="element-193" data-mp="3o5gs65a1oevm">
                <div className="grid gap-6 sm:grid-cols-2" data-id="element-194" data-mp="3o3vwgcbzupw6">
                  <div data-id="element-195" data-mp="3sxqoq45o0d6v">
                    <label className={labelClass} htmlFor="name" data-id="element-196" data-mp="3sy9z8zszjzav">
                      Full Name
                    </label>
                    <input id="name" name="name" type="text" value={form.name} onChange={event => update('name', event.target.value)} className={inputClass} autoComplete="name" data-id="element-197" data-mp="1sqk1mrrthwm" />
                  </div>
                  <div data-id="element-198" data-mp="2ck9xdjakxzkz">
                    <label className={labelClass} htmlFor="email" data-id="element-199" data-mp="2hhjwxrronrmj">
                      Email Address
                    </label>
                    <input id="email" name="email" type="email" value={form.email} onChange={event => update('email', event.target.value)} className={inputClass} autoComplete="email" data-id="element-200" data-mp="3krnzikez8txi" />
                  </div>
                  <div data-id="element-201" data-mp="7lnxielfke2f">
                    <label className={labelClass} htmlFor="phone" data-id="element-202" data-mp="7kvzyy89w69i">
                      Phone Number
                    </label>
                    <input id="phone" name="phone" type="tel" value={form.phone} onChange={event => update('phone', event.target.value)} className={inputClass} autoComplete="tel" data-id="element-203" data-mp="2mkraqtps1sv" />
                  </div>
                  <div data-id="element-204" data-mp="len8q4e6jm46">
                    <label className={labelClass} htmlFor="state" data-id="element-205" data-mp="lb7tn1kiad2r">
                      State
                    </label>
                    <select id="state" name="state" value={form.state} onChange={event => update('state', event.target.value)} className={inputClass} data-id="element-206" data-mp="19y73mg9erjkm">
                      <option value="" data-id="element-207" data-mp="101udttgzhpv5">Select a state</option>
                      {states.map(state => <option key={state} value={state} data-id="element-208" data-mp="qzcov79y2j4g">
                          {state}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-209" data-mp="h9jg0e16si3w">
                    <label className={labelClass} htmlFor="service" data-id="element-210" data-mp="h8qh1z8gywwb">
                      Service of Interest
                    </label>
                    <select id="service" name="service" value={form.service} onChange={event => update('service', event.target.value)} className={inputClass} data-id="element-211" data-mp="3ak2a1f7pnmi">
                      <option value="" data-id="element-212" data-mp="isb9n67b7fur">Select a service</option>
                      {services.map(service => <option key={service.slug} value={service.slug} data-id="element-213" data-mp="dury4e1zxh6i">
                          {service.title}
                        </option>)}
                      <option value="not-sure" data-id="element-214" data-mp="4sserf3ie10e">Not sure yet</option>
                    </select>
                  </div>
                  <fieldset data-id="element-215" data-mp="3w5dclxttnhiv">
                    <legend className={labelClass} data-id="element-216" data-mp="3r7lakazgqf0y">Preferred Contact Method</legend>
                    <div className="mt-4 flex items-center gap-6" data-id="element-217" data-mp="3r58slxtpg4nw">
                      {[{
                    value: 'email',
                    label: 'Email'
                  }, {
                    value: 'phone',
                    label: 'Phone'
                  }].map(option => <label key={option.value} className="flex items-center gap-2 text-[15px] text-ink/80" data-id="element-218" data-mp="3d93p6jx15em7">
                          <input type="radio" name="method" value={option.value} checked={form.method === option.value} onChange={event => update('method', event.target.value)} className="h-4 w-4 accent-navy-900" data-id="element-219" data-mp="1wqbrbo79q1nv" />
                          {option.label}
                        </label>)}
                    </div>
                  </fieldset>
                </div>

                <div className="mt-6" data-id="element-220" data-mp="1nr6tyl7g0eda">
                  <label className={labelClass} htmlFor="message" data-id="element-221" data-mp="1ns9hqk9gy0oz">
                    Message
                  </label>
                  <textarea id="message" name="message" rows={6} value={form.message} onChange={event => update('message', event.target.value)} className={inputClass} placeholder="Briefly describe the situation and what needs to be handled." data-id="element-222" data-mp="q96qoycisgfy" />
                </div>

                <input type="checkbox" name="botcheck" tabIndex={-1} aria-hidden="true" className="hidden" checked={Boolean(form.botcheck)} onChange={event => update('botcheck', event.target.checked ? 'true' : '')} data-id="element-223" data-mp="13ul6wv29jjl1" />

                {error && <p role="alert" className="mt-4 text-[14px] text-red-700" data-id="element-224" data-mp="kw0ijarrasb3">
                    {error}
                  </p>}

                <button type="submit" disabled={submitting} className="mt-7 flex items-center gap-3 bg-navy-900 px-8 py-4 text-[15px] font-medium text-white transition-colors duration-150 ease-out hover:bg-navy-800 disabled:cursor-not-allowed disabled:opacity-60" data-id="element-225" data-mp="3jl7oi9t8a3gb">
                  {submitting && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-226" data-mp="1wornes66sgoo" />}
                  {submitting ? 'Sending…' : 'Send Message'}
                </button>
                <p className="mt-4 text-[13px] leading-relaxed text-ink/60" data-id="element-227" data-mp="1mqv7zo67uzkr">
                  Submitting this form does not create a fiduciary or attorney-client relationship.
                </p>
              </form>}
          </div>

          <div data-id="element-228" data-mp="1dzb9n35hqrqb">
            <h2 className="font-serif text-[26px] text-navy-900" data-id="element-229" data-mp="1dwp6rgtjp0ef">What happens next</h2>
            <ul className="mt-7 space-y-7" data-id="element-230" data-mp="191jm0lbvprrx">
              {nextSteps.map(({
              icon: Icon,
              title,
              body
            }) => <li key={title} className="flex gap-4" data-id="element-231" data-mp="142q4mhth1gmh">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-232" data-mp="z2k92qk2z9z4" />
                  <div data-id="element-233" data-mp="pdndyi7nubdt">
                    <h3 className="text-[16px] font-medium text-navy-900" data-id="element-234" data-mp="pcbznogl9l8g">{title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-ink/75" data-id="element-235" data-mp="ue1ab2zwfww0">{body}</p>
                  </div>
                </li>)}
            </ul>

            <div className="mt-10 border-t border-slate-200 pt-8" data-id="element-236" data-mp="kek7ctc17x3q">
              <h2 className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-237" data-mp="kgwoj49yu3fq">
                DIRECT CONTACT
              </h2>
              <ul className="mt-5 space-y-4 text-[15px]" data-id="element-238" data-mp="1d37z97radkq9">
                <li className="flex items-start gap-3" data-id="element-239" data-mp="1d2pshh6zzo43">
                  <PhoneIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-240" data-mp="zcw40zzen5uw" />
                  <a href={company.phoneHref} className="text-ink/80 transition-colors duration-150 ease-out hover:text-navy-900" data-id="element-241" data-mp="13cwj8mubvl30">
                    {company.phoneDisplay}
                  </a>
                </li>
                <li className="flex items-start gap-3" data-id="element-242" data-mp="3gkeao652xf35">
                  <MailIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-243" data-mp="3giti2m2bs67x" />
                  <a href={`mailto:${company.email}`} className="text-ink/80 transition-colors duration-150 ease-out hover:text-navy-900" data-id="element-244" data-mp="3bkjhfvxu0dex">
                    {company.email}
                  </a>
                </li>
              </ul>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/70" data-id="element-245" data-mp="2xmbftapuonxf">
                {company.addressLine1}
                <br data-id="element-246" data-mp="2so09vb2oexkh" />
                {company.addressLine2}
                <br data-id="element-247" data-mp="37jzol4kajvyl" />
                {company.hours}
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>;
}